import React from "react";
import { Document, Page, Text, View, StyleSheet } from "@react-pdf/renderer";

const styles = StyleSheet.create({
  page: {
    fontFamily: "Helvetica",
    fontSize: 10,
    paddingTop: 36,
    paddingBottom: 40,
    paddingHorizontal: 44,
    lineHeight: 1.4,
    color: "#111",
  },
  brandBar: {
    height: 8,
    backgroundColor: "#C8102E",
    marginBottom: 20,
    marginHorizontal: -44,
    marginTop: -36,
  },
  brandName: {
    fontSize: 16,
    fontWeight: "bold",
    color: "#111",
    letterSpacing: 1.2,
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 9,
    color: "#555",
    marginBottom: 16,
  },
  docTitle: {
    fontSize: 13,
    fontWeight: "bold",
    color: "#C8102E",
    marginBottom: 14,
  },
  sectionTitle: {
    fontSize: 10,
    fontWeight: "bold",
    marginTop: 12,
    marginBottom: 6,
    borderBottomWidth: 1,
    borderBottomColor: "#ccc",
    paddingBottom: 4,
  },
  row: { marginBottom: 4, flexDirection: "row" },
  label: { width: "28%", color: "#555", fontSize: 9 },
  value: { width: "72%", fontSize: 9 },
  addressBlock: {
    fontSize: 9,
    color: "#222",
    marginBottom: 8,
    lineHeight: 1.45,
  },
  tableHead: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#000",
    paddingVertical: 6,
    marginTop: 6,
    fontWeight: "bold",
    fontSize: 9,
  },
  colItem: { width: "72%", paddingRight: 8 },
  colQty: { width: "28%", textAlign: "right" },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 0.5,
    borderBottomColor: "#e5e5e5",
    paddingVertical: 5,
    fontSize: 9,
  },
  footerNote: {
    marginTop: 24,
    fontSize: 8,
    color: "#777",
  },
  vehicleLine: { fontSize: 9, marginBottom: 4, paddingLeft: 4 },
});

function formatAddress(lines) {
  return lines.filter(Boolean).join(", ");
}

/**
 * @param {{ customer: object, items: { itemName: string, quantity: number|string }[], vehicles?: string[] }} props
 */
const CustomerInventoryPDF = ({ customer, items, vehicles }) => {
  const origin = formatAddress([
    customer.originAddress1,
    customer.originAddress2,
    [customer.originCity, customer.originState, customer.originPincode].filter(Boolean).join(", "),
    customer.originCountry,
  ]);

  const destination = formatAddress([
    customer.destinationAddress1,
    customer.destinationAddress2,
    [
      customer.destinationCity,
      customer.destinationState,
      customer.destinationPincode,
    ]
      .filter(Boolean)
      .join(", "),
    customer.destinationCountry,
  ]);

  const safeItems = (items || []).filter((it) => (it.itemName || "").trim());

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.brandBar} />
        <Text style={styles.brandName}>ACE RELOCATIONS</Text>
        <Text style={styles.subtitle}>Move inventory summary (customer copy)</Text>
        <Text style={styles.docTitle}>Your move details & inventory</Text>

        <Text style={styles.sectionTitle}>Contact</Text>
        <View style={styles.row}>
          <Text style={styles.label}>Name</Text>
          <Text style={styles.value}>{customer.customerName || "—"}</Text>
        </View>
        <View style={styles.row}>
          <Text style={styles.label}>Email</Text>
          <Text style={styles.value}>{customer.email || "—"}</Text>
        </View>
        <View style={styles.row}>
          <Text style={styles.label}>Phone</Text>
          <Text style={styles.value}>{customer.phoneNumber || "—"}</Text>
        </View>
        <View style={styles.row}>
          <Text style={styles.label}>Preferred move date</Text>
          <Text style={styles.value}>{customer.preferredMovingDate || "—"}</Text>
        </View>
        {customer.notes ? (
          <View style={styles.row}>
            <Text style={styles.label}>Notes</Text>
            <Text style={styles.value}>{customer.notes}</Text>
          </View>
        ) : null}

        <Text style={styles.sectionTitle}>Origin</Text>
        <Text style={styles.addressBlock}>{origin || "—"}</Text>

        <Text style={styles.sectionTitle}>Destination</Text>
        <Text style={styles.addressBlock}>{destination || "—"}</Text>

        {vehicles && vehicles.filter(Boolean).length > 0 ? (
          <>
            <Text style={styles.sectionTitle}>Vehicles (descriptions)</Text>
            {vehicles.filter(Boolean).map((v, i) => (
              <Text key={i} style={styles.vehicleLine}>
                • {v}
              </Text>
            ))}
          </>
        ) : null}

        <Text style={styles.sectionTitle}>Household / office inventory</Text>
        <View style={styles.tableHead}>
          <Text style={styles.colItem}>Item</Text>
          <Text style={styles.colQty}>Quantity</Text>
        </View>
        {safeItems.map((it, idx) => (
          <View key={idx} style={styles.tableRow} wrap={false}>
            <Text style={styles.colItem}>{it.itemName}</Text>
            <Text style={styles.colQty}>{String(it.quantity ?? "")}</Text>
          </View>
        ))}
        {safeItems.length === 0 ? (
          <Text style={{ fontSize: 9, color: "#777", marginTop: 8 }}>No line items recorded.</Text>
        ) : null}

        <Text style={styles.footerNote}>
          Generated from your online quote request. Final cubic feet and pricing will be confirmed by
          ACE Relocations.
        </Text>
      </Page>
    </Document>
  );
};

export default CustomerInventoryPDF;
